/**
 * アプリケーション全体の設定を管理
 */

// 環境変数の検証と取得
const getEnvVar = (key, defaultValue = null, required = false) => {
  const value = process.env[key];
  if (required && !value) {
    throw new Error(`環境変数 ${key} が設定されていません`);
  }
  return value || defaultValue;
};

const getBooleanEnv = (key, defaultValue = false) => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
};

// ===== 環境変数 =====
export const ENV_CONFIG = {
  LIFF_ID: getEnvVar('REACT_APP_LIFF_ID', '', true),
  API_BASE_URL: getEnvVar('REACT_APP_API_BASE_URL', '', true),
  WEBSOCKET_URL: getEnvVar('REACT_APP_WEBSOCKET_URL', '', true),
  DEBUG: getBooleanEnv('REACT_APP_DEBUG', false),
  APP_NAME: getEnvVar('REACT_APP_APP_NAME', 'Fujitec Elevator App'),
  VERSION: getEnvVar('REACT_APP_VERSION', '1.0.0'),
  NODE_ENV: process.env.NODE_ENV || 'development',
  BUILD_TIME: new Date().toISOString()
};

// ===== API エンドポイント =====
export const API_ENDPOINTS = {
  BASE: ENV_CONFIG.API_BASE_URL,
  WEBSOCKET: ENV_CONFIG.WEBSOCKET_URL,
  MY_ELEVATORS: `${ENV_CONFIG.API_BASE_URL}/my-elevators`,
  ELEVATOR_DETAILS: `${ENV_CONFIG.API_BASE_URL}/elevator-details`,
  CALL_ELEVATOR: `${ENV_CONFIG.API_BASE_URL}/call-elevator`,
  CALL_TOKEN: `${ENV_CONFIG.API_BASE_URL}/call-token`,
  ADD_ELEVATOR: `${ENV_CONFIG.API_BASE_URL}/add-elevator`,
  UPDATE_ELEVATOR: `${ENV_CONFIG.API_BASE_URL}/update-elevator`,
  DELETE_ELEVATOR: `${ENV_CONFIG.API_BASE_URL}/delete-elevator`
};

// ===== LIFF設定 =====
export const LIFF_CONFIG = {
  LIFF_ID: ENV_CONFIG.LIFF_ID,
  initOptions: {
    liffId: ENV_CONFIG.LIFF_ID
  }
};

// ===== レイアウト定数 =====
export const LAYOUT = {
  HEADER_HEIGHT: 'calc(100svh/72*7)',
  CONTENT_HEIGHT: 'calc(100svh/45*32)',
  FOOTER_HEIGHT: 'calc(100svh/24)',
  MARGIN_X: 'calc(100vw/12)',
  MARGIN_Y: 'calc(100svh/45*2)',
};

// ===== フロアリスト定数 =====
export const FLOOR_LIST = {
  CONTAINER_HEIGHT: 'calc(100svh/24*12)',
  PLACEHOLDER_HEIGHT: 'calc(100svh/72*7)',
  IMAGE_HEIGHT: 'h-6',
  CONTAINER_CLASS: 'flex flex-col items-center justify-center w-full',
};

// ===== カラー定数 =====
export const APP_COLORS = {
  // プライマリカラー
  UX_RED: '#D70024',
  UX_LIGHT_RED: '#F4ECED',
  UX_BLUE: '#0055DC',
  UX_LIGHT_BLUE: '#EDEFF2',

  // グレースケール
  UX_DARK_GRAY: '#666666',
  UX_GRAY: '#CCCCCC',
  UX_LIGHT_GRAY: '#F2F2F2',

  // セマンティックカラー
  PRIMARY: '#D70024',
  SECONDARY: '#0055DC',
  SUCCESS: '#3ECE44',
  WARNING: '#FFA500',
  ERROR: '#D70024',
  INFO: '#0055DC'
};

// ===== 選択モード定数 =====
export const SelectionMode = {
  CURRENT: 'current',
  DESTINATION: 'destination',
  CALL_READY: 'call_ready'
};

// ===== ラベル定数 =====
export const LABELS = {
  CURRENT: '現在',
  DESTINATION: '行先',
};

// ===== フロアタイプ定数 =====
export const FLOOR_TYPES = {
  CURRENT: 'current',
  DESTINATION: 'destination',
};

// ===== 運行状況 =====
export const ELEVATOR_STATUS_OVERRIDES = {
  '0': {
    status: {
      title: '通常運転中',
      message: '<span className="ux-text-asterisk">※乗場の液晶表示と異なる場合は、乗場の案内通りにご利用ください。</span>'
    }
  },
  '1': {
    status: {
      title: '運転休止中',
      message: '<span className="ux-text-no-asterisk">現在、エレベータは利用できません。<br />サービスセンターに連絡済みです。準備が整い次第、係員を派遣します。</span><br /><span className="ux-text-asterisk">※作業などの状況により到着が遅れる可能性があります。</span>'
    }
  },
  '2': {
    status: {
      title: '仮復旧運転中',
      message: '<span className="ux-text-no-asterisk">安全な速度で運転しています。</span>'
    }
  },
  '3': {
    status: {
      title: '点検中',
      message: '<span className="ux-text-no-asterisk">現在、エレベータは利用できません。<br />作業完了までお待ちください。完了時刻は次回停止予定日から確認ください。</span><br /><span className="ux-text-asterisk">※作業などの状況により予定時刻を前後する可能性があります。</span>'
    }
  },
  '99': {
    status: {
      title: '情報なし',
      message: '<span className="ux-text-no-asterisk">エレベータを選択してください。</span>'
    }
  },
};

// ===== トランジション定数 =====
export const TRANSITIONS = {
  MODAL_DURATION: 300,
  SCROLL_BEHAVIOR: 'auto',
};

// ===== UI定数 =====
export const UI_CONSTANTS = {
  // タイムアウト設定
  MODAL_ANIMATION_DURATION: 300,
  TOAST_DURATION: 3000,
  SMOOTH_SCROLL_DURATION: 300,
  GEOLOCATION_TIMEOUT: 10000,
  GEOLOCATION_MAX_AGE: 300000, // 5分

  // 再接続設定
  MAX_RECONNECT_ATTEMPTS: 5,
  RECONNECT_DELAY: 3000,
  CALL_TIMEOUT: 30000, // 30秒
};

// ===== バリデーション定数 =====
export const VALIDATION = {
  REG_NUMBER_PATTERN: /^\d{7}$/,
  REG_NUMBER_LENGTH: 7,
  ELEVATOR_NAME_MAX_LENGTH: 20
};

// ===== デバッグ用の設定情報表示 =====
if (ENV_CONFIG.DEBUG) {
  console.group('📋 アプリケーション設定');
  console.log('環境:', ENV_CONFIG.NODE_ENV);
  console.log('デバッグモード:', ENV_CONFIG.DEBUG);
  console.log('LIFF ID:', ENV_CONFIG.LIFF_ID);
  console.log('API Base URL:', ENV_CONFIG.API_BASE_URL);
  console.log('WebSocket URL:', ENV_CONFIG.WEBSOCKET_URL);
  console.log('バージョン:', ENV_CONFIG.VERSION);
  console.log('ビルド時刻:', ENV_CONFIG.BUILD_TIME);
  console.groupEnd();
}

// ===== 設定の凍結 =====
Object.freeze(ENV_CONFIG);
Object.freeze(API_ENDPOINTS);
Object.freeze(LIFF_CONFIG);
Object.freeze(LAYOUT);
Object.freeze(FLOOR_LIST);
Object.freeze(APP_COLORS);
Object.freeze(SelectionMode);
Object.freeze(LABELS);
Object.freeze(FLOOR_TYPES);
Object.freeze(TRANSITIONS);
Object.freeze(UI_CONSTANTS);
Object.freeze(VALIDATION);

// デフォルトエクスポート
const config = {
  ENV_CONFIG,
  API_ENDPOINTS,
  LIFF_CONFIG,
  LAYOUT,
  FLOOR_LIST,
  APP_COLORS,
  SelectionMode,
  LABELS,
  FLOOR_TYPES,
  TRANSITIONS,
  UI_CONSTANTS,
  VALIDATION
};
export default config;